import {
	Address,
	BDO,
	Client,
	ClientOrder,
	ClientType,
	Container,
	FullLooseOrder,
	LooseOrder,
	LooseOrderSubtype,
	Order,
	OrderType,
	OtherOrder,
	PaymentType,
	PriceType,
	TaskView,
	TaskType,
	User,
	Task,
	FinancesOrderView,
	ClientOrderToPayView,
	DeliveredLooseOrderView,
	PanelStatsView,
	OrderPriceChange,
	BdoStatement,
} from 'types';
import { Query } from './utils';
import moment from 'moment';
import { OrderWithPriceChanges } from 'types/order-with-price-changes.view';

async function send<T>(method: string, endpoint: string, data?: object, query?: object) {
	const response = await fetch(`${process.env['API']}/${endpoint}${query ? '?' + Query.encode(query) : ''}`, {
		method,
		headers: {
			'Content-Type': data ? 'application/json' : 'plain/text',
		},
		body: data ? JSON.stringify(data) : null,
		credentials: 'include',
	});

	if (endpoint !== 'logout' && (response.status === 401 || response.status === 403)) {
		(window.location as any) = '/logout';

		throw 'Sesja wygasła';
	}

	if (response.headers.get('content-type')?.match(/application\/json/g)) {
		if (response.status !== 200) throw await response.json();
		return (await response.json()) as T;
	} else {
		if (response.status !== 200) throw await response.text();
		return (await response.text()) as T;
	}
}

export interface CreateClientDto {
	name: string;
	type: ClientType;
	phone: string;
	email?: string;
	nip?: string;
	bdo?: string;
	street?: string;
	city?: string;
	postal?: string;
	description?: string;
}

export interface EditClientDto {
	name?: string;
	type?: ClientType;
	locked?: boolean;
	phone?: string;
	email?: string | null;
	nip?: string | null;
	bdo?: string | null;
	street?: string | null;
	city?: string | null;
	postal?: string | null;
	description?: string | null;
	removed?: null;
}

export interface CreatePaymentDto {
	orderId: number;
	orderType: OrderType;
	amount: number;
	type: PaymentType;
	date?: string;
}

export interface GetFinancesOrderDto {
	after: moment.Moment;
	before: moment.Moment;
	client_id?: number | undefined;
	paid?: boolean | undefined;
	payment_type?: PaymentType | undefined;
	closed?: boolean | undefined;
}

export interface CreateLooseOrderDto {
	subtype: LooseOrderSubtype;
	clientId: number;
	bdo: BDO;
	wasteType: string;
	address: string;
	contact: string | undefined;
	onPlace: boolean;
	count: number;
	price: number;
	wantedAdvance: number | null;
	priceType: PriceType;
	wantedPaymentType: PaymentType;
	deliveryDate: moment.Moment;
	claimingDate: moment.Moment | undefined;
	assignee: number | undefined;
	comments: string | undefined;
}

export interface EditLooseOrderDto {
	bdo?: BDO;
	wasteType?: string;
	address?: string;
	contact?: string;
	count?: number;
	price?: number | undefined;
	priceType?: PriceType | undefined;
	wantedAdvance?: number | null;
	wantedPaymentType?: PaymentType;
	comments?: string;
}

export interface CreateTaskDto {
	orderId: number;
	orderType: OrderType;
	type: TaskType;
	date: moment.Moment;
	itemsToClaim?: number;
	newPrice?: number;
}

export interface EditTaskDto {
	date?: moment.Moment;
	assignedTo?: number | null;
	takenAt?: moment.Moment;
	doneAt?: moment.Moment;
}

export interface CloseTaskDto {
	advance?: number;
	paidOrders: {
		id: number;
		type: OrderType;
	}[];
}

export default {
	sendCommand: async (type: string, payload: object, idempotencyKey?: string) => {
		return await send('POST', 'v2/commands', {
			type,
			payload,
			idempotencyKey: idempotencyKey ?? crypto.randomUUID(),
		});
	},

	login: async (username: string, password: string) => {
		try {
			return await send('POST', 'login', { username, password });
		} catch (e: any) {
			switch (e.error) {
				case 1:
					throw 'Niepoprawne dane logowania';

				default:
					throw `Nieznany błąd ${e.message || e.error || e}`;
			}
		}
	},

	logout: async () => {
		try {
			return await send('GET', 'logout');
		} catch (e: any) {
			switch (e.error) {
				default:
					throw `Nieznany błąd ${e.message || e.error || e}`;
			}
		}
	},

	users: {
		get: async (id: number, unsettled?: boolean, uncollected?: boolean) => {
			try {
				const result = await send('GET', `users/${id}`, undefined, {
					unsettled: unsettled ? '1' : '0',
					uncollected: uncollected ? '1' : '0',
				});

				return result as User;
			} catch (e: any) {
				switch (e.error) {
					case 3:
						throw 3;
					default:
						throw `Nieznany błąd ${e.message || e.error || e}`;
				}
			}
		},

		getAll: async (unsettled?: boolean, uncollected?: boolean) => {
			try {
				const result = await send('GET', `users`, undefined, {
					unsettled: unsettled ? '1' : '0',
					uncollected: uncollected ? '1' : '0',
				});

				return result as User[];
			} catch (e: any) {
				switch (e.error) {
					default:
						throw `Nieznany błąd ${e.message || e.error || e}`;
				}
			}
		},

		create: async (user_data) => {
			try {
				return await send('PUT', `users`, user_data);
			} catch (e: any) {
				switch (e.error) {
					case 1:
						throw 'Nazwa użytkownika jest zajęta';
					default:
						throw `Nieznany błąd ${e.message || e.error || e}`;
				}
			}
		},

		patch: async (id, data) => {
			try {
				return await send('PATCH', `users/${id}`, data);
			} catch (e: any) {
				switch (e.error) {
					case 1:
						throw 'Nazwa użytkownika zajęta';
					case 2:
						throw 'Hasło zbyt krótkie';
					case 3:
						throw 'Stare hasło jest nieprawidłowe';
					case 4:
						throw 'Nowe hasło nie może być takie samo jak stare';
					default:
						throw `Nieznany błąd ${e.message || e.error || e}`;
				}
			}
		},

		delete: async (id) => {
			try {
				return await send('DELETE', `users/${id}`);
			} catch (e: any) {
				switch (e.error) {
					default:
						throw `Nieznany błąd ${e.message || e.error || e}`;
				}
			}
		},

		history: async (id, limit) => {
			try {
				return await send('GET', `users/${id}/history`, undefined, { limit });
			} catch (e: any) {
				switch (e.error) {
					default:
						throw `Nieznany błąd ${e.message || e.error || e}`;
				}
			}
		},

		cards: {
			getAll: async ({ after, before, user_id }) => {
				try {
					return await send('GET', `users/cards`, undefined, { after, before, user_id });
				} catch (e: any) {
					switch (e.error) {
						default:
							throw `Nieznany błąd ${e.message || e.error || e}`;
					}
				}
			},

			put: async ({ mileage_start, mileage_end, time, plate, date }) => {
				try {
					return await send('PUT', `users/cards`, { mileage_start, mileage_end, time, plate, date });
				} catch (e: any) {
					switch (e.error) {
						default:
							throw `Nieznany błąd ${e.message || e.error || e}`;
					}
				}
			},
		},
	},

	containers: {
		sizes: {
			getAll: async () => {
				try {
					return await send('GET', 'containers/sizes');
				} catch (e: any) {
					switch (e.error) {
						default:
							throw `Nieznany błąd ${e.message || e.error || e}`;
					}
				}
			},

			create: async (name) => {
				try {
					return await send('PUT', 'containers/sizes', { name });
				} catch (e: any) {
					switch (e.error) {
						case 1:
							throw 'Taki rozmiar kontenera już istnieje';
						default:
							throw `Nieznany błąd ${e.message || e.error || e}`;
					}
				}
			},

			change: async (id, name) => {
				try {
					return await send('PATCH', `containers/sizes/${id}`, { name });
				} catch (e: any) {
					switch (e.error) {
						case 1:
							throw 'Taki rozmiar kontenera już istnieje';
						default:
							throw `Nieznany błąd ${e.message || e.error || e}`;
					}
				}
			},

			delete: async (id) => {
				try {
					return await send('DELETE', `containers/sizes/${id}`);
				} catch (e: any) {
					switch (e.error) {
						case 1:
							throw 'Nie ma takiego rozmiaru';
						case 2:
							throw 'Rozmiar jest przypisany do kontenera';
						case 3:
							throw 'Rozmiar jest przypisany do zamówienia';
						default:
							throw `Nieznany błąd ${e.message || e.error || e}`;
					}
				}
			},
		},

		get: async (id: number) => {
			try {
				const result = await send('GET', `containers/${id}`);

				return result as Container;
			} catch (e: any) {
				throw `Nieznany błąd ${e.message || e.error || e}`;
			}
		},

		getAll: async (states?: boolean) => {
			try {
				const result = await send('GET', 'containers', undefined, states ? { states } : undefined);

				return result as Container[];
			} catch (e: any) {
				throw `Nieznany błąd ${e.message || e.error || e}`;
			}
		},

		create: async (user_data) => {
			try {
				return await send('PUT', `containers`, user_data);
			} catch (e: any) {
				switch (e.error) {
					case 4:
						throw 'Numer kontenera już zajęty';
					default:
						throw `Nieznany błąd ${e.message || e.error || e}`;
				}
			}
		},

		patch: async (id, data) => {
			try {
				return await send('PATCH', `containers/${id}`, data);
			} catch (e: any) {
				switch (e.error) {
					default:
						throw `Nieznany błąd ${e.message || e.error || e}`;
				}
			}
		},

		delete: async (id) => {
			try {
				return await send('DELETE', `containers/${id}`);
			} catch (e: any) {
				switch (e.error) {
					case 3:
						throw 'Kontener jest przypisany do zadania';
					default:
						throw `Nieznany błąd ${e.message || e.error || e}`;
				}
			}
		},

		history: async (id, limit) => {
			try {
				return await send('GET', `containers/${id}/history`, undefined, { limit });
			} catch (e: any) {
				switch (e.error) {
					default:
						throw `Nieznany błąd ${e.message || e.error || e}`;
				}
			}
		},
	},

	clients: {
		getAll: async () => {
			try {
				return await send<Client[]>('GET', `clients`);
			} catch (e: any) {
				throw `Nieznany błąd ${e.message || e.error || e}`;
			}
		},

		get: async (id: number) => {
			try {
				return await send<Client>('GET', `clients/${id}`);
			} catch (e: any) {
				throw `Nieznany błąd ${e.message || e.error || e}`;
			}
		},

		create: async (data: CreateClientDto) => {
			try {
				return await send('PUT', `clients`, data);
			} catch (e: any) {
				if (e.error?.includes('name already exists')) {
					throw 'Nazwa klienta jest zajęta';
				}

				throw `Nieznany błąd ${e.message || e.error || e}`;
			}
		},

		patch: async (id: number, data: EditClientDto) => {
			try {
				return await send('PATCH', `clients/${id}`, data);
			} catch (e: any) {
				if (e.error?.includes('name already exists')) {
					throw 'Nazwa klienta jest zajęta';
				}

				throw `Nieznany błąd ${e.message || e.error || e}`;
			}
		},

		delete: async (id: number) => {
			try {
				return await send('DELETE', `clients/${id}`);
			} catch (e: any) {
				if (e.contains('Client has unclosed orders')) {
					throw 'Klient ma co najmniej jedno otwarte zlecenie';
				}

				throw `Nieznany błąd ${e.message || e.error || e}`;
			}
		},

		history: async (id: number) => {
			try {
				return await send('GET', `clients/${id}/history`);
			} catch (e: any) {
				throw `Nieznany błąd ${e.message || e.error || e}`;
			}
		},
	},

	client: (id: number) => ({
		patch: async (data: EditClientDto) => {
			try {
				return await send('PATCH', `clients/${id}`, data);
			} catch (e: any) {
				throw `Nieznany błąd ${e.message || e.error || e}`;
			}
		},

		delete: async () => {
			try {
				return await send('DELETE', `clients/${id}`);
			} catch (e: any) {
				switch (e.error) {
					case 2:
						throw 'Klient ma co najmniej jedno otwarte zlecenie';

					default:
						throw `Nieznany błąd ${e.message || e.error || e}`;
				}
			}
		},
	}),

	expenses: {
		getAll: async ({ user_id, before, after, company }) => {
			try {
				return await send('GET', `expenses`, undefined, { user_id, before, after, company });
			} catch (e: any) {
				switch (e.error) {
					default:
						throw `Nieznany błąd ${e.message || e.error || e}`;
				}
			}
		},

		create: async (expense_data) => {
			try {
				return await send('PUT', `expenses`, expense_data);
			} catch (e: any) {
				switch (e.error) {
					default:
						throw `Nieznany błąd ${e.message || e.error || e}`;
				}
			}
		},

		settle: async (id) => {
			try {
				return await send('PATCH', `expenses/${id}`, { settled: 1 });
			} catch (e: any) {
				switch (e.error) {
					case 2:
						throw `Wydatek jest już rozliczony`;
					default:
						throw `Nieznany błąd ${e.message || e.error || e}`;
				}
			}
		},

		expenseTypes: {
			getAll: async () => {
				try {
					return await send('GET', `expenseTypes`);
				} catch (e: any) {
					switch (e.error) {
						default:
							throw `Nieznany błąd ${e.message || e.error || e}`;
					}
				}
			},

			put: async (name) => {
				try {
					return await send('PUT', `expenseTypes`, { name });
				} catch (e: any) {
					switch (e.error) {
						default:
							throw `Nieznany błąd ${e.message || e.error || e}`;
					}
				}
			},
		},

		settlements: {
			getAll: async ({ user_id, before, after }) => {
				try {
					return await send('GET', `settlements`, undefined, { user_id, before, after });
				} catch (e: any) {
					switch (e.error) {
						default:
							throw `Nieznany błąd ${e.message || e.error || e}`;
					}
				}
			},
			create: async (settlement) => {
				try {
					return await send('PUT', `settlements`, settlement);
				} catch (e: any) {
					switch (e.error) {
						default:
							throw `Nieznany błąd ${e.message || e.error || e}`;
					}
				}
			},
		},
	},

	payments: {
		put: async (data: CreatePaymentDto) => {
			try {
				return await send('PUT', `payments`, data);
			} catch (e: any) {
				switch (e.error) {
					default:
						throw `Nieznany błąd ${e.message || e.error || e}`;
				}
			}
		},
	},

	orders: {
		getAll: async ({
			client_id,
			after,
			before,
			closed,
			advances,
		}: {
			client_id?: number | undefined;
			after?: number | undefined;
			before?: number | undefined;
			closed?: boolean | undefined;
			advances?: boolean | undefined;
		}) => {
			try {
				return await send<any>('GET', `orders`, undefined, { client_id, after, before, closed, advances });
			} catch (e: any) {
				switch (e.error) {
					case 3:
						throw 3;
					default:
						throw `Nieznany błąd ${e.message || e.error || e}`;
				}
			}
		},

		getByRelatedId: async (relatedId: number): Promise<any[]> => {
			try {
				return await send<any>('GET', 'orders', undefined, { related_id: relatedId });
			} catch (e: any) {
				throw `Nieznany błąd ${e.message || e.error || e}`;
			}
		},

		get: async (id) => {
			try {
				return await send('GET', `orders/${id}`);
			} catch (e: any) {
				switch (e.error) {
					case 3:
						throw 3;
					default:
						throw `Nieznany błąd ${e.message || e.error || e}`;
				}
			}
		},

		create: async (order_data) => {
			try {
				return await send('PUT', 'orders', order_data);
			} catch (e: any) {
				switch (e.error) {
					case 5:
						throw 'Nie można dodać zamówienia, ponieważ inna wymiana została już dodana';

					default:
						throw `Nieznany błąd ${e.message || e.error || e}`;
				}
			}
		},

		patch: async (id, data) => {
			try {
				return await send('PATCH', `orders/${id}`, data);
			} catch (e: any) {
				switch (e.error) {
					default:
						throw `Nieznany błąd ${e.message || e.error || e}`;
				}
			}
		},

		delete: async (id) => {
			try {
				return await send('DELETE', `orders/${id}`);
			} catch (e: any) {
				switch (e.error) {
					default:
						throw `Nieznany błąd ${e.message || e.error || e}`;
				}
			}
		},

		owns: {
			getAll: async ({
				after,
				before,
				client_id,
				to,
			}: {
				after?: number | undefined;
				before?: number | undefined;
				client_id?: number | undefined;
				to?: boolean | undefined;
			}) => {
				try {
					return await send<any>('GET', 'owns', undefined, { before, after, client_id, to });
				} catch (e: any) {
					switch (e.error) {
						default:
							throw `Nieznany błąd ${e.message || e.error || e}`;
					}
				}
			},

			get: async (id) => {
				try {
					return await send('GET', `owns/${id}`);
				} catch (e: any) {
					switch (e.error) {
						default:
							throw `Nieznany błąd ${e.message || e.error || e}`;
					}
				}
			},

			put: async ({ client_id, address, to, date }) => {
				try {
					return await send('PUT', 'owns', { client_id, address, to, date });
				} catch (e: any) {
					switch (e.error) {
						default:
							throw `Nieznany błąd ${e.message || e.error || e}`;
					}
				}
			},

			patch: async (id, { weight, date }) => {
				try {
					return await send('PATCH', `owns/${id}`, { weight, date });
				} catch (e: any) {
					switch (e.error) {
						default:
							throw `Nieznany błąd ${e.message || e.error || e}`;
					}
				}
			},

			contractors: {
				getAll: async () => {
					try {
						return await send('GET', `owns/contractors`);
					} catch (e: any) {
						switch (e.error) {
							default:
								throw `Nieznany błąd ${e.message || e.error || e}`;
						}
					}
				},
			},
		},

		incomes: {
			getAll: async ({ after, before, client_id }: { after?: number; before?: number; client_id?: number }) => {
				try {
					return await send<OtherOrder[]>('GET', 'incomes', undefined, { before, after, client_id });
				} catch (e: any) {
					throw `Nieznany błąd ${e.message || e.error || e}`;
				}
			},

			get: async (id: number) => {
				try {
					return await send<OtherOrder>('GET', `incomes/${id}`);
				} catch (e: any) {
					throw `Nieznany błąd ${e.message || e.error || e}`;
				}
			},

			put: async ({ client_id, name, value, value_type, comments, date }) => {
				try {
					return await send('PUT', 'incomes', { client_id, name, value, value_type, comments, date });
				} catch (e: any) {
					switch (e.error) {
						default:
							throw `Nieznany błąd ${e.message || e.error || e}`;
					}
				}
			},

			patch: async (id, { name, value, value_type, comments, date }) => {
				try {
					return await send('PATCH', `incomes/${id}`, { name, value, value_type, comments, date });
				} catch (e: any) {
					switch (e.error) {
						default:
							throw `Nieznany błąd ${e.message || e.error || e}`;
					}
				}
			},
		},
	},

	tasks: {
		getAll: async ({ after, before, assignee }: { after: moment.Moment; before: moment.Moment; assignee?: number }) => {
			try {
				return await send<TaskView[]>('GET', `tasks`, undefined, {
					after: after.toISOString(),
					before: before.toISOString(),
					assignee,
				});
			} catch (e: any) {
				throw `Nieznany błąd ${e.message || e.error || e}`;
			}
		},

		get: async (id: number) => {
			try {
				return await send<Task>('GET', `tasks/${id}`);
			} catch (e: any) {
				throw `Nieznany błąd ${e.message || e.error || e}`;
			}
		},

		patch: async (id: number, data: EditTaskDto) => {
			try {
				return await send<Task>('PATCH', `tasks/${id}`, {
					...data,
					date: data.date?.toISOString(),
				});
			} catch (e: any) {
				throw `Nieznany błąd ${e.message || e.error || e}`;
			}
		},

		put: async (taskData: CreateTaskDto) => {
			try {
				return await send<CreateTaskDto>('PUT', `tasks`, {
					...taskData,
					date: taskData.date.toISOString(),
				});
			} catch (e: any) {
				throw `Nieznany błąd ${e.message || e.error || e}`;
			}
		},

		close: async (id: number, dto: CloseTaskDto) => {
			try {
				return await send<Task>('POST', `tasks/${id}/close`, dto);
			} catch (e: any) {
				throw `Nieznany błąd ${e.message || e.error || e}`;
			}
		},

		delete: async (id: number) => {
			try {
				return await send('DELETE', `tasks/${id}`);
			} catch (e: any) {
				throw `Nieznany błąd ${e.message || e.error || e}`;
			}
		},
	},

	stats: {
		panel: async () => {
			try {
				return await send<PanelStatsView>('GET', 'stats/panel', undefined, {
					day_start: moment().hours(0).minutes(0).seconds(0).subtract(1, 'second').unix() * 1000,
					day_end: moment().hours(23).minutes(59).seconds(59).unix() * 1000,
				});
			} catch (e: any) {
				switch (e.error) {
					default:
						throw `Nieznany błąd ${e.message || e.error || e}`;
				}
			}
		},

		summary: async (from, to) => {
			try {
				return await send('GET', 'stats/summary', undefined, { from, to });
			} catch (e: any) {
				switch (e.error) {
					default:
						throw `Nieznany błąd ${e.message || e.error || e}`;
				}
			}
		},

		incomes: async ({ after, before }) => {
			try {
				return await send('GET', 'stats/incomes', undefined, { after, before });
			} catch (e: any) {
				switch (e.error) {
					default:
						throw `Nieznany błąd ${e.message || e.error || e}`;
				}
			}
		},
	},

	addresses: {
		getAll: async ({ clientId }) => {
			try {
				const result = await send('GET', 'addresses', undefined, { client_id: clientId });

				return result as Address[];
			} catch (e: any) {
				switch (e.error) {
					default:
						throw `Nieznany błąd ${e.message || e.error || e}`;
				}
			}
		},

		add: async ({ name, clientId }) => {
			try {
				const result = await send('PUT', 'addresses', { name, client_id: clientId });

				return result as Address;
			} catch (e: any) {
				switch (e.error) {
					default:
						throw `Nieznany błąd ${e.message || e.error || e}`;
				}
			}
		},

		delete: async (id) => {
			try {
				return await send('DELETE', `addresses/${id}`);
			} catch (e: any) {
				switch (e.error) {
					default:
						throw `Nieznany błąd ${e.message || e.error || e}`;
				}
			}
		},
	},

	looseOrders: {
		create: async (dto: CreateLooseOrderDto) => {
			try {
				return await send<FullLooseOrder>('PUT', 'v2/orders/loose', {
					...dto,
					deliveryDate: dto.deliveryDate.toISOString(),
					claimingDate: dto.claimingDate?.toISOString(),
				});
			} catch (e: any) {
				throw `Nieznany błąd ${e.message || e.error || e}`;
			}
		},

		getDelivered: async () => {
			try {
				return await send<DeliveredLooseOrderView[]>('GET', 'v2/orders/loose/delivered');
			} catch (e: any) {
				throw `Nieznany błąd ${e.message || e.error || e}`;
			}
		},
	},

	looseOrder: (id: number) => ({
		get: async () => {
			try {
				return await send<FullLooseOrder>('GET', `v2/orders/loose/${id}`);
			} catch (e: any) {
				throw `Nieznany błąd ${e.message || e.error || e}`;
			}
		},

		patch: async (data: EditLooseOrderDto) => {
			try {
				return await send<FullLooseOrder>('PATCH', `v2/orders/loose/${id}`, {
					...data,
				});
			} catch (e: any) {
				throw `Nieznany błąd ${e.message || e.error || e}`;
			}
		},

		take: async () => {
			try {
				const result = await send('POST', `v2/orders/loose/${id}/take`);

				return result as LooseOrder;
			} catch (e: any) {
				throw `Nieznany błąd ${e.message || e.error || e}`;
			}
		},

		resign: async () => {
			try {
				const result = await send('POST', `v2/orders/loose/${id}/resign`);

				return result as LooseOrder;
			} catch (e: any) {
				throw `Nieznany błąd ${e.message || e.error || e}`;
			}
		},

		execute: async (paid: boolean) => {
			try {
				const result = await send('POST', `v2/orders/loose/${id}/execute`, { paid });

				return result as LooseOrder;
			} catch (e: any) {
				throw `Nieznany błąd ${e.message || e.error || e}`;
			}
		},

		assign: async (assignee: number | null) => {
			try {
				const result = await send('POST', `v2/orders/loose/${id}/assign`, { assignee });

				return result as LooseOrder;
			} catch (e: any) {
				throw `Nieznany błąd ${e.message || e.error || e}`;
			}
		},

		delete: async () => {
			try {
				return await send('DELETE', `v2/orders/loose/${id}`);
			} catch (e: any) {
				throw `Nieznany błąd ${e.message || e.error || e}`;
			}
		},
	}),

	orders2: {
		client: {
			get: async (id: number) => {
				try {
					return await send<ClientOrder[]>('GET', `v2/orders/client/${id}`);
				} catch (e: any) {
					throw `Nieznany błąd ${e.message || e.error || e}`;
				}
			},

			toPay: async (id: number) => {
				try {
					return await send<ClientOrderToPayView[]>('GET', `v2/orders/client/${id}/to_pay`);
				} catch (e: any) {
					throw `Nieznany błąd ${e.message || e.error || e}`;
				}
			},
		},

		finances: {
			get: async (data: GetFinancesOrderDto) => {
				try {
					return await send<FinancesOrderView[]>('GET', 'v2/orders/finances', undefined, {
						...data,
						after: data.after.toISOString(),
						before: data.before.toISOString(),
					});
				} catch (e: any) {
					throw `Nieznany błąd ${e.message || e.error || e}`;
				}
			},
		},

		getAll: async ({
			clientId,
			after,
			before,
			done,
		}: {
			clientId?: number | undefined;
			after?: moment.Moment | undefined;
			before?: moment.Moment | undefined;
			done?: boolean | undefined;
		}) => {
			try {
				return await send<Order[]>('GET', `v2/orders`, undefined, {
					client_id: clientId,
					after: after?.toISOString(),
					before: before?.toISOString(),
					done,
				});
			} catch (e: any) {
				throw `Nieznany błąd ${e.message || e.error || e}`;
			}
		},

		order: (orderType: OrderType, orderId: number) => ({
			priceChanges: async () => {
				try {
					return await send<OrderPriceChange[]>('GET', `v2/orders/${orderType}/${orderId}/price_changes`);
				} catch (e: any) {
					throw `Nieznany błąd ${e.message || e.error || e}`;
				}
			},

			statement: async () => {
				try {
					return await send<BdoStatement>('GET', `v2/orders/${orderType}/${orderId}/statement`);
				} catch (error: any) {
					if (error.error === 'Not Found') {
						return null;
					}

					throw `Nieznany błąd ${error.message || error.error || error}`;
				}
			},

			statementPdf: async () => {
				const response = await fetch(`${process.env['API']}/v2/orders/${orderType}/${orderId}/statement/pdf`, {
					method: 'GET',
					credentials: 'include',
				});

				if (response.status === 401 || response.status === 403) {
					(window.location as any) = '/logout';

					throw 'Sesja wygasła';
				}

				if (response.status !== 200) {
					throw `Nie udało się pobrać podglądu PDF (${response.status})`;
				}

				return await response.blob();
			},
		}),

		priceChanges: async ({ after, before }: { after: moment.Moment; before: moment.Moment }) => {
			try {
				return await send<OrderWithPriceChanges[]>('GET', `v2/orders/price_changes`, undefined, {
					after: after.toISOString(),
					before: before.toISOString(),
				});
			} catch (e: any) {
				throw `Nieznany błąd ${e.message || e.error || e}`;
			}
		},
	},
};
